/**
 * Dashboard roles of the XZLLENZ (legacy) instance – PO 2026-10-08: admin, manager, housekeeping, support.
 * The one list of the dashboard (review #229 P3): access check (AuthContext), user management (role selection,
 * POST /api/users). It mirrors LEGACY_DASHBOARD_ROLES in backend/shared/tenancy/tenant-claim.ts, the default
 * Firestore rules and the API gateway; a consistency test in business-services compares all of them.
 */
export const TASK_MANAGEMENT_ROLES: import('../types').UserRole[] = ['admin', 'manager'];

export const DASHBOARD_ROLES = ['admin', 'manager', 'housekeeping', 'support'] as const;
export type DashboardRole = typeof DASHBOARD_ROLES[number];

/**
 * One German label per stored role value, shown identically in both tenants (PO 28.10.2026 "gleiche Rollen", Refs #622).
 * Stored values stay untouched (claims, Firestore rules, API gateway): XZLLENZ stores admin/manager/housekeeping/support,
 * a cell stores owner/manager/staff/viewer. admin and owner are the same right (hasDashboardRole maps admin -> owner),
 * so both read "Inhaber"; the cell role staff carries exactly the rights of housekeeping and support together.
 */
export const ROLE_LABELS = {
  admin: 'Inhaber',
  owner: 'Inhaber',
  manager: 'Manager',
  housekeeping: 'Reinigung',
  support: 'Support',
  staff: 'Reinigung / Support',
  viewer: 'Nur lesen',
} as const;
export type LabelledRole = keyof typeof ROLE_LABELS;

/** Roles offered in the user management, in the display order Inhaber, Manager, Reinigung, Support, Nur lesen. */
export const SELECTABLE_ROLES = {
  legacy: DASHBOARD_ROLES,
  cell: ['owner', 'manager', 'staff', 'viewer'],
} as const;

export function isDashboardRole(role: unknown): role is DashboardRole {
  return DASHBOARD_ROLES.some(allowed => allowed === role);
}

/**
 * Support console operator role from the dashboard (profile) role – review #231 P3-6: never from token claims.
 * Values of OperatorRole (types/support.types.ts). Without a dashboard role: 'agent' (least privilege,
 * the previous default of every XZLLENZ user, who carries no role claim).
 */
export function operatorRoleFor(role: unknown): 'admin' | 'supervisor' | 'agent' {
  if (role === 'admin') return 'admin';
  if (role === 'manager') return 'supervisor';
  return 'agent';
}
